import { BaseController } from 'controllers/BaseController'
import {
  BomItemActivityFilter,
  CreateRFQFromProjectCommandRequest,
  SendRequestForQuotationRequest,
  UpdateRfqBoMItemsRequest,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { GetContext } from 'store/storeUtils'
import { RFQLine } from './Components/ProjectRFQLines'
import { IProjectRFQAPI, ProjectRFQAPI } from './ProjectRFQAPI'

export class ProjectRFQController extends BaseController<IProjectRFQAPI> {
  private _projectId: string
  constructor() {
    let pId = null
    super((onRequestChanged) => {
      const { partyId, projectId } = GetContext()
      pId = projectId
      return new ProjectRFQAPI(partyId, projectId, onRequestChanged)
    })
    this._projectId = pId
  }

  public async getRFQs() {
    try {
      return await this.api.getRFQs({
        orderBy: 'DateChanged desc' as any,
      })
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async getRFQById(id: string) {
    try {
      return await this.api.getRFQById(id)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async createRequestForQuotation(draftRFQLines: RFQLine[]) {
    try {
      const groupedBySupplier = draftRFQLines.reduce((acc, line) => {
        const supplierId = line.supplier?.id || 'no-supplier'

        if (acc[supplierId]) {
          acc[supplierId].push(line)
        } else {
          acc[supplierId] = [line]
        }

        return acc
      }, {})

      const requests = []
      for (const supplierId in groupedBySupplier) {
        const linesForSupplier = groupedBySupplier[supplierId]

        const bomItemAndActivitiesFilter: BomItemActivityFilter[] =
          linesForSupplier.map((line) => ({
            bomItemId: line.bomItemPointer.id,
            activities: line.activities.map((activity) => ({
              workingStepType: activity.workingStepType,
            })),
          })) as BomItemActivityFilter[]

        var req: CreateRFQFromProjectCommandRequest = {
          buyerProjectId: this._projectId,
          bomItemAndActivities: bomItemAndActivitiesFilter.filter(
            (x) => x.activities && x.activities.length > 0,
          ),
          forwardId: linesForSupplier[0]?.supplier?.glNumber,
          forwardSender: '1777401147609', // TODO: Remove hardcoding when forwarding functionality is implemented,
        }

        requests.push(this.api.createRequestForQuotation(req))
      }

      return await Promise.all(requests)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async sendRequestForQuotation(req: SendRequestForQuotationRequest) {
    try {
      return await this.api.sendRequestForQuotation(req)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async updateRfqBomItems(
    rfqId: string,
    workingStepToDelete: WorkingStepType,
    currentBomItems: BomItemActivityFilter[],
  ) {
    try {
      const filteredBomItems = currentBomItems
        .map((bomItem) => ({
          bomItemId: bomItem.bomItemId,
          activities: (bomItem.activities ?? []).filter(
            (activity) => activity.workingStepType !== workingStepToDelete,
          ),
        }))
        .filter((bomItem) => bomItem.activities.length > 0)

      const hasChanges =
        filteredBomItems.length !== currentBomItems.length ||
        filteredBomItems.some((bomItem, index) => {
          const currentActivities = currentBomItems[index]?.activities ?? []
          return bomItem.activities.length !== currentActivities.length
        })

      if (!hasChanges) {
        return
      }

      const req: UpdateRfqBoMItemsRequest = {
        bomItemAndActivities: filteredBomItems,
      }

      return await this.api.updateRfqBomItems(rfqId, req)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async deleteRFQ(rfqId: string) {
    try {
      await this.api.deleteRfq(rfqId)
    } catch (err) {
      throw this.HandleError(err)
    }
  }
}
